const en = {
  app: {
    title: 'BFPE Token DApp',
    subtitle: 'Web3 application to interact with the BFPE token on Hyperledger Besu',
  },
  nav: {
    tokens: 'Tokens',
    nfts: 'NFTs',
    tutorial: 'Tutorial',
    docs: 'Documentation',
  },
  lang: {
    es: 'Castellano',
    eu: 'Euskera',
    en: 'English',
  },
  wallet: {
    connect: 'Connect Wallet',
    connecting: 'Connecting...',
    connected: 'Wallet connected',
    chainId: 'Chain ID',
    address: 'Address',
    balance: 'Balance',
    copy: 'Copy full address',
    copied: 'Copied!',
    switchNetwork: 'Switch to Besu network (ID: {chainId})',
    disconnect: 'Disconnect',
    // WalletConnect component
    title: 'Wallet Connection',
    connectedBadge: 'Connected',
    switchBtn: 'Switch to Besu network (Chain ID: {chainId})',
    description: 'Connect your wallet to interact with the BFPE Token contract on Hyperledger Besu.',
    note: 'Note:',
    noteText: 'Make sure MetaMask is installed and configured to connect to your Hyperledger Besu network.',
    addressLabel: 'Account Address',
    chainIdLabel: 'Chain ID',
    notDetected: 'Not detected',
    disconnectBtn: 'Disconnect Wallet',
  },
  tokenInfo: {
    title: 'Token Information',
    refresh: 'Refresh',
    name: 'Name',
    symbol: 'Symbol',
    totalSupply: 'Total Supply',
    yourBalance: 'Your Balance',
    status: 'Status',
    paused: 'Paused',
    active: 'Active',
    contractAddress: 'Contract Address',
    configRequired: 'Configuration required:',
    configMessage: 'Please set the contract address in the .env file',
  },
  transfer: {
    title: 'Transfer Tokens',
    noWallet: 'Connect your wallet to transfer tokens',
    recipientLabel: 'Recipient Address',
    recipientPlaceholder: '0x...',
    amountLabel: 'Amount (BFPE)',
    amountPlaceholder: '0.0',
    errNoWallet: 'Please connect your wallet first',
    errInvalidAddress: 'Invalid recipient address',
    errInvalidAmount: 'Invalid amount',
    successTitle: 'Transfer successful!',
    successTx: 'TX: {hash}',
    toastLoading: 'Transferring tokens...',
    toastSuccess: '{amount} BFPE successfully transferred!',
    toastError: 'Error transferring tokens',
    processing: 'Processing...',
    submit: 'Transfer Tokens',
  },
  faucet: {
    title: 'Token Faucet',
    noWallet: 'Connect your wallet to request free tokens',
    amountLabel: 'Amount per Request',
    cooldownLabel: 'Cooldown',
    hours: '{n} hours',
    nextRequest: 'Next request available in:',
    availableNow: 'You can request tokens now!',
    errNoWallet: 'Please connect your wallet first',
    errCooldown: 'You must wait before requesting more tokens',
    successTitle: 'Tokens successfully received!',
    successTx: 'TX: {hash}',
    toastLoading: 'Requesting tokens from faucet...',
    toastSuccess: 'Tokens successfully received!',
    toastError: 'Error requesting tokens from faucet',
    processing: 'Processing...',
    submit: 'Request {amount} BFPE',
    infoPrefix: 'ℹ️ Info:',
    infoText: 'You can request free tokens every {hours} hours. Tokens will be automatically transferred to your wallet after confirming the transaction.',
    contractLabel: 'BFPE Token Contract',
    copyAddress: 'Copy address',
    importMetaMask: 'Import BFPE in MetaMask',
  },
  mintBurn: {
    title: 'Mint & Burn',
    noWallet: 'Connect your wallet to mint or burn tokens',
    mintTitle: 'Mint Tokens',
    burnTitle: 'Burn Tokens',
    recipientLabel: 'Recipient Address',
    recipientPlaceholder: '0x...',
    amountLabel: 'Amount (BFPE)',
    burnAmountLabel: 'Amount to Burn (BFPE)',
    amountPlaceholder: '0.0',
    errNoWallet: 'Please connect your wallet first',
    errInvalidAddress: 'Invalid address',
    errInvalidAmount: 'Invalid amount',
    mintSuccessTitle: 'Mint successful!',
    burnSuccessTitle: 'Burn successful!',
    successTx: 'TX: {hash}',
    mintToastLoading: 'Minting tokens...',
    mintToastSuccess: '{amount} BFPE successfully minted!',
    mintToastError: 'Error minting tokens. Make sure you are the owner.',
    burnToastLoading: 'Burning tokens...',
    burnToastSuccess: '{amount} BFPE successfully burned!',
    burnToastError: 'Error burning tokens',
    processing: 'Processing...',
    mintSubmit: 'Mint Tokens',
    burnSubmit: 'Burn Tokens',
  },
  admin: {
    title: 'Admin Panel',
    noWallet: 'Connect your wallet to access the admin panel',
    notOwner: 'Only the contract owner can access these functions.',
    isOwner: '✅ You are the contract owner',
    transfersTitle: 'Transfer Control',
    currentStatus: 'Current status:',
    paused: 'Paused',
    active: 'Active',
    successTitle: 'Operation successful!',
    successTx: 'TX: {hash}',
    resumeToastLoading: 'Resuming transfers...',
    resumeToastSuccess: 'Transfers resumed!',
    pauseToastLoading: 'Pausing transfers...',
    pauseToastSuccess: 'Transfers paused!',
    pauseToastError: 'Error changing pause state',
    processing: 'Processing...',
    resumeBtn: 'Resume Transfers',
    pauseBtn: 'Pause Transfers',
    ownershipTitle: 'Transfer Ownership',
    newOwnerLabel: 'New Owner Address',
    newOwnerPlaceholder: '0x...',
    errInvalidAddress: 'Invalid address',
    ownershipSuccessTitle: 'Ownership transferred!',
    ownershipToastLoading: 'Transferring ownership...',
    ownershipToastSuccess: 'Ownership successfully transferred!',
    ownershipToastError: 'Error transferring ownership',
    warningLabel: '⚠️ Warning:',
    warningText: 'This action is irreversible. You will lose all administrator privileges.',
    ownershipSubmit: 'Transfer Ownership',
  },
  events: {
    title: 'On-Chain Events',
    pause: 'Pause',
    resume: 'Resume',
    clear: 'Clear',
    waiting: 'Waiting for events... Transactions will appear here.',
    unknownEvent: '{event} at block {block}',
    mintMsg: 'Mint {value} BFPE → {to}',
    burnMsg: 'Burn {value} BFPE ← {from}',
    transferMsg: 'Transfer {value} BFPE: {from} → {to}',
    faucetMsg: 'Faucet {amount} BFPE → {to}',
  },
  tutorial: {
    title: 'Interactive Tutorial',
    subtitle: 'Learn to use the BFPE DApp step by step',
    progress: 'Tutorial Progress',
    completed: '{done}/{total} completed',
    prev: 'Previous',
    next: 'Next',
    markDone: 'Completed ✓',
    practice: 'Go to practice →',
    tip: '💡 Tip:',
    steps: [
      {
        title: '1. Connect your Wallet',
        tab: 'tokens',
        icon: '🦊',
        content: 'To interact with the blockchain, you need a wallet. MetaMask is the most popular one. Install it as a browser extension and connect it by clicking "Connect Wallet" in the top right corner.',
        tip: 'MetaMask must be configured for the BFPE Mainnet network: RPC URL https://bfpe-mainnet.blockchain.tkn.eus, Chain ID 1337, Symbol: BFPE. The app itself will offer to switch networks automatically if it detects you are on a different one.',
      },
      {
        title: '2. Request Faucet Tokens',
        tab: 'tokens',
        icon: '💧',
        content: 'A faucet is a mechanism that distributes free tokens for testing. Go to the Tokens section and click "Request" to receive BFPE tokens. There is a cooldown between requests to prevent abuse.',
        tip: 'On real networks, faucets only exist on testnets so developers can test their contracts at no cost. On BFPE Mainnet the faucet is educational and limited by the contract owner.',
      },
      {
        title: '3. Transfer Tokens',
        tab: 'tokens',
        icon: '📤',
        content: 'Send BFPE tokens to another Ethereum address. Enter the recipient address (0x... format) and the amount. The transaction will be recorded on the blockchain permanently and immutably.',
        tip: 'Each transfer generates a "Transfer" event in the ERC20 contract. You can see the full transaction history in the block explorer: esploratzaile.blockchain.tkn.eus',
      },
      {
        title: '4. Token Mint & Burn',
        tab: 'tokens',
        icon: '🔥',
        content: 'The contract owner can mint new BFPE tokens and increase the total supply. Any user can burn their own tokens, permanently destroying them and reducing the supply.',
        tip: 'Mint increases the totalSupply and burn reduces it. This mechanism is the basis of a token\'s monetary policy. Only the owner (0x9e6d0B27762BfCD7d8913C9474583d715672ee87) can mint new tokens.',
      },
      {
        title: '5. Admin Panel',
        tab: 'tokens',
        icon: '🛡️',
        content: 'If you connect the contract owner\'s wallet, you will see the Admin Panel. From there you can pause all token transfers, configure the faucet cooldown and amount, or transfer ownership to another address.',
        tip: 'OpenZeppelin\'s Ownable pattern ensures only one address has administrator privileges. When the contract is paused, no transfer can be executed — not even the owner can transfer tokens.',
      },
      {
        title: '6. Mint an NFT',
        tab: 'nfts',
        icon: '🎨',
        content: 'NFTs (Non-Fungible Tokens) are unique and non-repeatable tokens. Go to the NFTs section, enter a metadata URI (link to a JSON or image) and click "Mint NFT". The minting price is 0.5 BFPE.',
        tip: 'MetaMask will request two transactions: first approve 0.5 BFPE spending to the NFT contract (approve ERC20), then the minting operation. Metadata is usually a JSON on IPFS with fields: name, description and image.',
      },
      {
        title: '7. Manage your Collection',
        tab: 'nfts',
        icon: '📦',
        content: 'In "My NFTs" you will see all the NFTs you own with their unique Token ID. You can import them to MetaMask with the "Import to MetaMask" button to view them in your wallet, or list them on the marketplace with a BFPE price.',
        tip: 'Listing an NFT requires two transactions (Step 1/2 and Step 2/2): first you authorize the Marketplace contract to transfer your NFT on your behalf (approve ERC721), then you register the listing with the desired price.',
      },
      {
        title: '8. NFT Marketplace',
        tab: 'nfts',
        icon: '🏪',
        content: 'The decentralized marketplace allows users to buy and sell NFTs. Sellers list their NFTs with a BFPE price. To buy, the buyer approves BFPE spending and then confirms the purchase.',
        tip: 'Payment is made in BFPE tokens, not native ETH (the Besu network has no native ETH). The platform charges a 2.5% fee per sale that goes to the owner. Marketplace contract: 0x6b3afb8A91fB704ACb187A96057fc1B28Ae24B0B',
      },
      {
        title: '9. Events Panel',
        tab: 'tokens',
        icon: '📡',
        content: 'The events panel shows in real time all operations occurring on the blockchain: transfers, mints, burns, faucet requests... It updates automatically with each new block.',
        tip: 'Solidity events are the main way to index what happens on a blockchain. Block explorers like esploratzaile.blockchain.tkn.eus use them to build the transaction history.',
      },
      {
        title: '10. Block Explorer',
        tab: 'tokens',
        icon: '🔍',
        content: 'The BFPE network has its own explorer where you can see all transactions, deployed contracts, tokens and events. Access it at esploratzaile.blockchain.tkn.eus to inspect any operation.',
        tip: 'Active contracts on the network: BFPE Token (0xf556f877Eb8E66393e6F044B2196490A267967bD), NFT BFPENFT (0x0230fF20f9A10359e97683732694a947B1e9EfF5), Marketplace (0x6b3afb8A91fB704ACb187A96057fc1B28Ae24B0B).',
      },
    ],
  },
  mintNFT: {
    title: 'Mint NFT',
    noWallet: 'Connect your wallet to mint NFTs',
    mintPriceLabel: 'Minting price:',
    contractLabel: 'NFT Contract:',
    dragDrop: 'Drag a file here or click to select',
    formats: 'Formats: JPG, PNG, GIF, WEBP, PDF · Images up to 20 MB (compressed to 5 MB) · PDFs up to 5 MB',
    fileAriaLabel: 'Select file for NFT',
    previewAlt: 'Preview',
    removeAriaLabel: 'Remove selected file',
    compressionWarning: 'The image exceeds 5 MB and will be automatically compressed on upload.',
    uploadSuccess: 'File uploaded successfully',
    uploadError: 'Error uploading file',
    errFormat: 'Format not allowed. Allowed formats: JPG, PNG, GIF, WEBP, PDF',
    errPdfSize: 'PDF files cannot exceed 5 MB',
    errImgSize: 'Images cannot exceed 20 MB',
    errNoWallet: 'Please connect your wallet first',
    errNoFile: 'Please upload a file first',
    approveLoading: 'Approving BFPE spending...',
    approveSuccess: 'Approval confirmed',
    mintLoading: 'Minting NFT...',
    mintSuccess: 'NFT successfully minted!',
    mintError: 'Error minting NFT',
    uploadingBtn: 'Uploading...',
    uploadingCompressingBtn: 'Uploading and compressing...',
    uploadBtn: 'Upload file',
    mintingBtn: 'Minting...',
    mintBtn: 'Mint NFT · {price} BFPE',
    mintSuccessTitle: 'NFT successfully minted!',
    viewExplorer: 'View in explorer',
    nameLabel: 'NFT Name',
    namePlaceholder: 'E.g. My first artwork',
    descLabel: 'Description (optional)',
    descPlaceholder: 'Describe your NFT...',
    errNoName: 'NFT name is required',
  },
  myNFTs: {
    title: 'My NFTs',
    noWallet: 'Connect your wallet to view your NFTs',
    empty: 'You have no NFTs yet',
    emptySubtext: 'Mint your first NFT to get started',
    nftTitle: 'NFT #{id}',
    tokenId: 'Token ID: {id}',
    creator: 'Creator: {address}',
    listed: 'Listed',
    importBtn: 'Import to MetaMask',
    tokenUriLabel: 'Token URI:',
    pricePlaceholder: 'Price in BFPE',
    errInvalidPrice: 'Please enter a valid price',
    errNoMetaMask: 'MetaMask is not available',
    errImport: 'Could not import NFT: {message}',
    step1Loading: 'Step 1/2 — Authorizing Marketplace to transfer the NFT...',
    step1Success: 'Step 1/2 completed ✓',
    step2Loading: 'Step 2/2 — Publishing NFT on the Marketplace...',
    step2Success: 'NFT published on the Marketplace!',
    listingBtn: 'Listing...',
    listBtn: 'List on Marketplace',
  },
  marketplace: {
    title: 'NFT Marketplace',
    noWallet: 'Connect your wallet to view the marketplace',
    empty: 'No NFTs listed',
    emptySubtext: 'Be the first to list an NFT',
    nftTitle: 'NFT #{id}',
    seller: 'Seller: {address}',
    creator: 'Creator: {address}',
    tokenUri: 'Token URI:',
    priceLabel: 'Price',
    yourNFT: 'Your NFT',
    approveLoading: 'Approving BFPE spending...',
    approveSuccess: 'Approval confirmed',
    buyLoading: 'Buying NFT...',
    buySuccess: 'NFT successfully purchased!',
    buyingBtn: 'Buying...',
    buyBtn: 'Buy NFT',
  },
  nftPage: {
    configTitle: 'Configuration Required',
    configMessage: 'Please configure the NFT contract addresses in the .env file:',
    configNote: 'Run',
    configNote2: 'to deploy the contracts.',
    title: 'NFT Collection & Marketplace',
    subtitle: 'Mint, collect and trade NFTs',
  },
  docs: {
    title: 'Documentation',
    subtitle: 'Key concepts of Blockchain, Smart Contracts and Web3',
    categories: [
      {
        category: 'Basic Concepts',
        items: [
          {
            title: 'What is Blockchain?',
            content: 'A blockchain is a distributed digital ledger that records transactions across a network of computers. Each block contains a hash of the previous block, creating an immutable chain. There is no central server; all nodes maintain an identical copy.',
          },
          {
            title: 'What is Hyperledger Besu?',
            content: 'Hyperledger Besu is an open-source Ethereum client developed under the Linux Foundation. It is compatible with the EVM (Ethereum Virtual Machine) and supports both public and private/permissioned networks. Ideal for enterprise and educational environments.',
          },
          {
            title: 'What is a Wallet?',
            content: 'A wallet (like MetaMask) stores your private keys and allows you to sign transactions. Your public address (0x...) is like your account number, and your private key is like your password. NEVER share your private key.',
          },
          {
            title: 'What is Gas?',
            content: 'Gas is the unit that measures the computational work needed to execute operations on the EVM. Each operation has a gas cost. On our Besu development network, the gas price is 0, so transactions are free.',
          },
        ],
      },
      {
        category: 'Tokens & Standards',
        items: [
          {
            title: 'ERC-20 (Fungible Token)',
            content: 'ERC-20 is the standard for fungible (interchangeable) tokens. It defines functions like transfer(), approve(), transferFrom(), balanceOf(), totalSupply(). Our BFPE Token implements this standard using OpenZeppelin, with additional features like mint, burn, pause and faucet.',
          },
          {
            title: 'ERC-721 (NFT)',
            content: 'ERC-721 is the standard for non-fungible (unique) tokens. Each token has a unique ID and can have associated metadata (image, name, etc.). Key functions: ownerOf(), safeTransferFrom(), approve(), tokenURI(). Our BFPENFT implements ERC721 + ERC721URIStorage + ERC721Enumerable.',
          },
          {
            title: 'What is OpenZeppelin?',
            content: 'OpenZeppelin is a library of audited and tested smart contracts. It provides standard implementations of ERC-20, ERC-721, access control (Ownable), security (ReentrancyGuard, Pausable) and more. Using OpenZeppelin instead of custom implementations greatly reduces the risk of vulnerabilities.',
          },
        ],
      },
      {
        category: 'Smart Contracts',
        items: [
          {
            title: 'What is a Smart Contract?',
            content: 'A smart contract is a program that runs on the blockchain. Once deployed, its code is immutable. Users interact with it by sending transactions. The contract has a state (variables) that is permanently stored on the blockchain.',
          },
          {
            title: 'What is an ABI?',
            content: 'The ABI (Application Binary Interface) is the interface that defines how to interact with a smart contract. It is a JSON that describes the functions, their parameters and return types. The frontend needs the ABI to know what functions it can call and how to encode/decode data.',
          },
          {
            title: 'Events',
            content: 'Events are logs that smart contracts emit during execution. They are cheap to store and allow applications to "listen" to what happens on the blockchain. For example, each Transfer emits an event with from, to and value.',
          },
          {
            title: 'Modifiers',
            content: 'Modifiers are decorators that add conditions to functions. For example, onlyOwner requires that only the contract owner can execute the function. whenNotPaused prevents execution when the contract is paused.',
          },
        ],
      },
      {
        category: 'Marketplace & DeFi',
        items: [
          {
            title: 'How does the Marketplace work?',
            content: '1) The seller approves the marketplace to transfer their NFT (approve). 2) Calls listNFT() with the price. 3) The buyer calls buyNFT() sending ETH. 4) The contract transfers the NFT to the buyer, the payment to the seller (minus the fee), and the fee to the marketplace owner.',
          },
          {
            title: 'Approve + Transfer Pattern',
            content: 'In Ethereum, a contract cannot "take" tokens directly. The owner must first approve the contract to move their tokens. This two-step pattern is fundamental in both ERC-20 and ERC-721.',
          },
          {
            title: 'ReentrancyGuard',
            content: 'Reentrancy is an attack where a malicious contract calls a function again before the first execution finishes (the famous "The DAO" hack). OpenZeppelin\'s ReentrancyGuard prevents this by blocking recursive calls.',
          },
        ],
      },
      {
        category: 'Development Tools',
        items: [
          {
            title: 'Hardhat',
            content: 'Hardhat is a development environment for Ethereum. It allows compiling Solidity contracts, running tests, deploying contracts and debugging. It includes a local network for development. In this project we use it to compile and deploy on Besu.',
          },
          {
            title: 'Web3.js',
            content: 'Web3.js is a JavaScript library that allows interaction with Ethereum nodes. It provides methods for sending transactions, calling contract functions, querying balances, listening to events, etc. It is the bridge between the frontend and the blockchain.',
          },
          {
            title: 'MetaMask',
            content: 'MetaMask is a browser extension that acts as a wallet and Web3 provider. It injects the window.ethereum object that allows DApps to request access to user accounts and sign transactions.',
          },
          {
            title: 'Docker',
            content: 'Docker allows running the entire infrastructure (Besu, deployer, frontend) in isolated containers. This facilitates reproducibility: any student can spin up the complete environment with a single command.',
          },
        ],
      },
    ],
  },
};

export default en;
